import {BCMSRecord,BCMSCurrency, useModel } from "bcms-react";
import {Account} from "model";

/**
 * @class StopPayment
 * @classdesc Class definition for controlling the properties that are set in the search workflow
 * 
 * @property {account} _account - The account the payment is in
 * @property {STRING} _paymentType - if payment is single or range
 * @property {STRING} _checkNumber - The check number to stop if single check
 * @property {STRING} _startCheckNumber - The first check if range
 * @property {STRING} _endCheckNumber - The last check if range
 * @property {bcmsCurrency} _amount - the amount field only for single type 
 * @property {STRING} _reason - The reason stop payment is created 
 * @property {ARRAY} _stopCheckCustomFields - Array of custom field js objects which are looped over to create the ui. 
 * The fields for a stop payment previously static and predefined, but now they are arbitray. 
 * There can be an indefinite amount of fields with indefinite names and indefinite field types.
 * @property {ARRAY} _stopRangeCustomFields - Array of custom field js objects which are looped over to create the ui. 
 * The fields for a stop payment previously static and predefined, but now they are arbitray. 
 * There can be an indefinite amount of fields with indefinite names and indefinite field types.
 */
export default class StopPayment {
    _account;
    _paymentType;
    _checkNumber;
    _startCheckNumber;
    _endCheckNumber;
    _amount;
    _reason;
    _stopCheckCustomFields;
    _stopRangeCustomFields;
    
    static account = "0";
    static paymentType = "Single Check"; 
    static checkNumber = "1001";
    static startCheckNumber = "1001";
    static endCheckNumber = "1010";
    static amount = "100";
    static reason = "Check Issued for Incorrect Amount";
    static stopCheckCustomFields = [];
    static stopRangeCustomFields = [];

    constructor({ account, paymentType, checkNumber, startCheckNumber, endCheckNumber, amount, reason, stopCheckCustomFields, stopRangeCustomFields }) {
        this.account = account ?? "";
        this.paymentType = paymentType ?? "Single Check";
        this.checkNumber = checkNumber ?? "";
        this.startCheckNumber = startCheckNumber ?? "";
        this.endCheckNumber = endCheckNumber ?? "";
        this.amount = amount  ?? "";
        this.reason = reason ?? "--Select a Reason--";
        this.stopCheckCustomFields = stopCheckCustomFields ?? [];
        this.stopRangeCustomFields = stopRangeCustomFields ?? [];
    }
    
    /**
    * @function setDefaults
    * @param {VOID} - No arguments passed.
    * @returns {VOID} - Nothing is returned.
    * @description Sets the properties of the StopPayment instance to default values if nothing was 
    * changed by the user.
    * @memberof StopPayment
    */
    setDefaults() {
        this._account = this._account === "" ? Account.initByType(StopPayment.account) : this._account; 
        this._paymentType = this._paymentType === ""  ? StopPayment.paymentType : this._paymentType; 
        //this._checkNumber = this._checkNumber === ""  ? StopPayment.checkNumber : this._checkNumber; 
        this._startCheckNumber = this._startCheckNumber === ""  ? StopPayment.startCheckNumber : this._startCheckNumber; 
        this._endCheckNumber = this._endCheckNumber === ""  ? StopPayment.endCheckNumber : this._endCheckNumber;
        //this._amount = this._amount === ""  ? StopPayment.amount : this._amount; 
        this._reason = this._reason === "--Select a Reason--"  ? StopPayment.reason : this._reason; 

        // Custom fields were added to this class, so the values
        // in those fields will set other values in this class for now.
        // For "Single Check", "Amount" and "Check number" are optional 
        // if that's how they are branded and are shown as empty if the user did not enter values.
        if(this._paymentType === "Single Check") {
            this._stopCheckCustomFields.forEach(field => {
                const fieldType = field.fieldType.toLowerCase();
                const fieldIsOptional = field.fieldIsOptional;
    
                if(fieldType.includes("check amount") && !fieldIsOptional) {
                    this._amount = this._amount === "" ? StopPayment.amount : this._amount; 
                }
                if(fieldType.includes("check number") && !fieldIsOptional) {
                    this._checkNumber = this._checkNumber === ""  ? StopPayment.checkNumber : this._checkNumber; 
                }
            });
        } else if(this._paymentType === "Range of Checks") {
            this._amount = StopPayment.amount;
        }
    }

    // START: Static functions for creating the stop payment custom fields.
    /**
    * @function getCustomFieldOptionKeysByType
    * @param {OBJECT} branding - The model branding object.
    * @param {STRING} type - The option keys for "stop_check_field" or "stop_range_field".
    * @returns {ARRAY} - An array of option keys "stop_check_field" or "stop_range_field".
    * @description Returns an array of option keys prefixed as "stop_check_field" or "stop_range_field".
    * @memberof StopPayment
    */
    static getCustomFieldOptionKeysByType(branding, type) {
        return Object.keys(branding.options).reduce((acc, optionKey) => {
            if(optionKey.includes(type)) acc.push(optionKey);
            return acc;
        }, []);
    }

    /**
    * @function getArrayOfCustomFieldObjects
    * @param {OBJECT} branding - The model branding object.
    * @param {ARRAY} arrayOfBrandingOptions - Array of option keys prefixed as "stop_check_field" or "stop_range_field".
    * @returns {ARRAY} - An array of objects that define each custom field that we will loop over to create the ui.
    * @description Returns an array of objects that define each custom field that we will loop over to create the ui.
    * @memberof StopPayment
    */
    static getArrayOfCustomFieldObjects(branding, arrayOfBrandingOptions) {
      return arrayOfBrandingOptions.reduce((acc, optionKey, index) => {
        let optionValue = branding.options[optionKey];
        const optionValueFormatted = optionValue.toLowerCase();

        acc.push({
          id: index,
          brandingOption: optionKey,
          brandingOptionValue: optionValue,
          fieldType: optionValue,
          fieldIsOptional: optionValueFormatted.includes(`optional`),
          showFieldInConfirmationPage: true, // could be true/false
          enteredValue: optionValue.includes("Reason") ? "--Select a Reason--" : undefined
        });
        return acc;
      }, []);
    }

    /**
    * @function getArrayOfCustomFieldObjectsByBrandingOptionType
    * @param {OBJECT} branding - The model branding object.
    * @param {STRING} type - Branding option keys prefixed as "stop_check_field" or "stop_range_field".
    * @returns {ARRAY} - An array of objects that define each custom field that we will loop over to create the ui.
    * @description Returns an array of objects that define each custom field that we will loop over to create the ui.
    * @memberof StopPayment
    */
    static getArrayOfCustomFieldObjectsByBrandingOptionType(branding, type) {
        const optionKeys = StopPayment.getCustomFieldOptionKeysByType(branding, type);
        return StopPayment.getArrayOfCustomFieldObjects(branding, optionKeys);
    }
    // END: Static functions for creating the stop payment custom fields.

    /**
    * @function setCustomFieldValue
    * @param {ARRAY} fields - An array of custom field objects.
    * @param {STRING} fieldKey - A key that identifies a custom field object, like "Check Amount", whose value will be updated.
    * @param {ANY} value - The value we will set for a custom field object's "enteredValue" property.
    * @returns {ARRAY} - Returns an array of objects with an updated "enteredValue".
    * @description Returns an array of objects with an updated "enteredValue".
    * @memberof StopPayment
    */
    static setCustomFieldValue(fields, fieldKey, value) {
        const _fields = fields.map(field => Object.assign({}, field));
        
        const _fieldKey = fieldKey.toLowerCase();
        return _fields.reduce((acc, field) => {
            const fieldType = field.fieldType.toLowerCase();
            if(fieldType.includes(_fieldKey)) field.enteredValue = value;
            acc.push(field);
            return acc;
        }, []);
    }

    /**
    * 
    * Getters:
    * 
    */

    /**
      * @name account
      * @instance
      * @property get {account} - returns an instance of an account  
      * @memberof StopPayment
      */
    get account() { return this._account; }

    /**
     * @name paymentType
     * @instance
     * @property get {STRING} - returns the paymentType
     * @memberof StopPayment
     */
    get paymentType() { return this._paymentType; }

    /**
     * @name checkNumber
     * @instance
     * @property get {STRING} - returns the checkNumber
     * @memberof StopPayment
     */
    get checkNumber() { return this._checkNumber; }

    /**
     * @name startCheckNumber
     * @instance
     * @property get {STRING} - returns the starting check number
     * @memberof StopPayment
     */
    get startCheckNumber() { return this._startCheckNumber; }

    /**
     * @name endCheckNumber
     * @instance
     * @property get {STRING} - returns the ending check number
     * @memberof StopPayment
     */
    get endCheckNumber() { return this._endCheckNumber; }
    /**
     * @name amount
     * @instance
     * @property get {bcmsCurrency} - returns the amount as an instance of BCMScurrency
     * @memberof StopPayment
     */

    get amount() { return this._amount; }
    /**
     * @name reason
     * @instance
     * @property set {STRING} - gets the reason
     * @memberof StopPayment 
     */
    get reason() { return this._reason; }

    get stopCheckCustomFields() { return this._stopCheckCustomFields; }

    get stopRangeCustomFields() { return this._stopRangeCustomFields; }

    /**
     * 
     * Setters:
     * 
     */


    /**
      * @name account
      * @instance
      * @property get {account} - returns an instance of an account  
      * @memberof StopPayment
      */
    set account(value) {
        if (value instanceof BCMSRecord || value === "") {
            this._account= value;
        } else if (isNaN(value) === false && value !== "") {
            this._account = Account.initByType(value)
        }
    }
    /**
     * @name paymentType
     * @instance
     * @property get {STRING} - returns the paymentType
     * @memberof StopPayment
     */
    set paymentType(value) { this._paymentType = value; }

    /**
     * @name checkNumber
     * @instance
     * @property get {STRING} - returns the checkNumber
     * @memberof StopPayment
     */
    set checkNumber(value) { return this._checkNumber = value; }

    /**
     * @name startCheckNumber
     * @instance
     * @property get {STRING} - returns the starting check number
     * @memberof StopPayment
     */
    set startCheckNumber(value) { return this._startCheckNumber = value; }

    /**
     * @name endCheckNumber
     * @instance
     * @property get {STRING} - returns the ending check number
     * @memberof StopPayment
     */
    set endCheckNumber(value) { return this._endCheckNumber = value; }
    /**
     * @name amount
     * @instance
     * @property get {bcmsCurrency} - returns the amount as an instance of BCMScurrency
     * @memberof StopPayment
     */

    set amount(value) {
        if (isNaN(value) === false && value !== "") {
            this._amount = BCMSCurrency.init(value);
        } else if (value === "") {
            this._amount = "";
        } else if (value instanceof BCMSCurrency) {
            this._amount = value;
        }
    }
    /**
     * @name reason
     * @instance
     * @property set {STRING} - gets the reason
     * @memberof StopPayment 
     */
    set reason(value) { return this._reason = value; }

    set stopCheckCustomFields(value) { return this._stopCheckCustomFields = value; }

    set stopRangeCustomFields(value) { return this._stopRangeCustomFields = value; }

    static init(record) {
        return new StopPayment(record ?? {});
    }
}